import apiService from './api.service';

export interface Game {
  id: string;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  thumbnail: string;
  banner: string;
  category: string;
  difficulty: 'Fácil' | 'Médio' | 'Difícil';
  duration_minutes: number;
  xp_reward: number;
  min_players: number;
  max_players: number;
  is_multiplayer: boolean;
  is_active: boolean;
  total_plays: number;
  avg_rating: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface GameSession {
  id: string;
  game: Game;
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  current_players: number;
  participants: Array<{
    id: string;
    name: string;
    avatar: string;
  }>;
  created_at: string;
}

export interface GameResult {
  id: string;
  game_session: string;
  score: number;
  xp_earned: number;
  position: number;
  completed: boolean;
  created_at: string;
}

class GamesService {
  /**
   * Lista todos os games disponíveis
   */
  async getAllGames(): Promise<Game[]> {
    const response = await apiService.get<Game[]>('/games/');
    return response.data;
  }

  /**
   * Obtém detalhes de um game específico
   */
  async getGame(gameId: string): Promise<Game> {
    const response = await apiService.get<Game>(`/games/${gameId}/`);
    return response.data;
  }

  /**
   * Inicia uma nova sessão de game
   */
  async startGameSession(gameId: string): Promise<GameSession> {
    const response = await apiService.post<GameSession>(`/games/${gameId}/start/`);
    return response.data;
  }

  /**
   * Entra em uma sessão de game existente
   */
  async joinGameSession(sessionId: string): Promise<GameSession> {
    const response = await apiService.post<GameSession>(`/game-sessions/${sessionId}/join/`);
    return response.data;
  }

  /**
   * Envia resultado de um game
   */
  async submitGameResult(sessionId: string, data: { score: number; completed: boolean }): Promise<GameResult> {
    const response = await apiService.post<GameResult>(`/game-sessions/${sessionId}/submit-result/`, data);
    return response.data;
  }

  /**
   * Lista sessões de game do usuário
   */
  async getMyGameSessions(status?: GameSession['status']): Promise<GameSession[]> {
    const params = status ? { status } : {};
    const response = await apiService.get<GameSession[]>('/game-sessions/my/', { params });
    return response.data;
  }

  /**
   * Obtém sessões agendadas
   */
  async getScheduledSessions(): Promise<GameSession[]> {
    const response = await apiService.get<GameSession[]>('/game-sessions/scheduled/');
    return response.data;
  }
}

export const gamesService = new GamesService();
